import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Button, Input, Select, EmptyState, Modal } from "@/admin/ui";
import { toast } from "sonner";
import { ActionIcon, StatusPill, PriorityPill } from "@/user/workspace/wsui";
import { fmtDate, fmtBytes, downloadAttachment } from "@/user/workspace/ws.api";
import { ceoApi } from "@/user/ceo/ceoApi";
import DecisionCards from "@/user/ceo/DecisionCards";
import { MoneyMeterPanel } from "@/user/ceo/MoneyMeter";

const TABS = [
    ["overview", "Overview"], ["departments", "Departments"], ["activity", "Business Activity"],
    ["submissions", "Submissions"], ["documents", "Documents"], ["tasks", "Tasks"],
    ["meetings", "Meetings"], ["cards", "Decision Cards"],
];

const AI_PLACEHOLDERS = [
    ["CEO Chat", "Ask questions across your whole company in plain language."],
    ["Decision Cards AI", "Auto-generated, ranked decisions with confidence."],
    ["Money Meter", "Live financial health score with future-impact simulation."],
    ["Business Scan AI", "Upload a file and get an instant business read-out."],
    ["Business Predictions", "Forecast revenue, cash and risk weeks ahead."],
    ["Department AI Agents", "A dedicated agent working inside every department."],
];

function greeting() {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

export default function CeoCommandCenter() {
    const { user, company } = useOutletContext();
    const nav = useNavigate();
    const [tab, setTab] = useState("overview");
    const [ov, setOv] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);

    useEffect(() => { ceoApi.overview().then(setOv).catch(() => {}); }, []);

    return (
        <div data-testid="ceo-command-center">
            <header className="mb-7">
                <div className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                    <span className="h-px w-6 bg-neutral-300" />
                    Command Center
                </div>
                <div className="mt-3 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-black">
                            {greeting()}, <span className="italic font-normal">{(user.name || "").split(" ")[0]}</span>
                        </h1>
                        <p className="mt-2 text-[14px] text-neutral-600">
                            {company?.name} · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                            {ov && <> · <span className="text-black font-medium">{ov.active_employees}</span> active employees · <span className="uppercase text-violet-700 text-[12px] font-semibold">{ov.company_status}</span></>}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                    <Button variant="primary" onClick={() => nav("/app/business-scan")} data-testid="ceo-business-scan-btn">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2M4 12h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        Business Scan
                    </Button>
                    <Button variant="ghost" onClick={() => nav("/app/chat")} data-testid="ceo-back-to-chat">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h9a4 4 0 014 4v8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                        Back to Chat
                    </Button>
                    <Button variant="ghost" onClick={() => setSearchOpen(true)} data-testid="ceo-search-btn">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.7" /><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
                        Search company
                    </Button>
                    </div>
                </div>
            </header>

            <div className="flex gap-1.5 overflow-x-auto pb-2 mb-7 border-b border-black/[0.06]" data-testid="ceo-tabs">
                {TABS.map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)} data-testid={`ceo-tab-${key}`}
                        className={`shrink-0 text-[13px] font-medium px-3.5 py-2 rounded-full transition-colors ${tab === key ? "bg-black text-white" : "text-neutral-600 hover:bg-black/[0.04]"}`}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === "overview" && <OverviewTab ov={ov} />}
            {tab === "departments" && <DepartmentsTab />}
            {tab === "activity" && <ActivityTab />}
            {tab === "submissions" && <SubmissionsTab />}
            {tab === "documents" && <DocumentsTab />}
            {tab === "tasks" && <TasksTab />}
            {tab === "meetings" && <MeetingsTab />}
            {tab === "cards" && <DecisionCards />}

            {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
        </div>
    );
}

/* ---------- Overview ---------- */
function OverviewTab({ ov }) {
    const nav = useNavigate();
    const [health, setHealth] = useState(null);
    const [mm, setMm] = useState(null);
    useEffect(() => { ceoApi.health().then(setHealth).catch(() => {}); }, []);
    useEffect(() => { ceoApi.moneyMeter().then(setMm).catch(() => {}); }, []);

    const cards = [
        ["Total Employees", ov?.total_employees, "departments"],
        ["Departments Enabled", ov?.departments_enabled, "departments"],
        ["Submissions Today", ov?.submissions_today, "submissions"],
        ["Pending Reports", ov?.pending_reports, "submissions"],
        ["Files Today", ov?.files_today, "documents"],
        ["Meetings", ov?.meetings_scheduled, "meetings"],
        ["Open Tasks", ov?.open_tasks, "tasks"],
    ];

    const money = (v) => (v == null ? "Waiting for AI Analysis" : `₹${Number(v).toLocaleString("en-IN")}`);

    return (
        <div className="space-y-9">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {cards.map(([label, val, t], i) => (
                    <div key={i} data-testid={`ceo-ov-${i}`} className="glass rounded-2xl p-4 hover:-translate-y-0.5 transition-all duration-300">
                        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-neutral-500 truncate">{label}</div>
                        <div className="mt-2 font-display text-2xl font-medium text-black">{val ?? "—"}</div>
                    </div>
                ))}
            </div>

            <MoneyMeterPanel data={mm} onSimulate={(d) => ceoApi.moneyMeterSimulate(d)} />

            <Section title="Company Health" hint="Values from your data. AI-derived metrics arrive next phase.">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[["Revenue", money(health?.revenue)], ["Expenses", money(health?.expenses)], ["Cash", money(health?.cash)], ["Employees", health?.employees ?? "—"], ["Reports", health?.reports ?? "—"], ["Business Health", "Waiting for AI Analysis"]].map(([l, v], i) => (
                        <div key={i} data-testid={`ceo-health-${i}`} className="glass rounded-2xl p-4">
                            <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-neutral-500">{l}</div>
                            <div className={`mt-2 font-display leading-tight ${String(v).startsWith("Waiting") ? "text-[12px] text-neutral-400 font-normal" : "text-xl font-medium text-black"}`}>{v}</div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section title="Quick Actions">
                <div className="flex flex-wrap gap-2">
                    {[["sales", "Sales"], ["finance", "Finance"], ["hr", "HR"], ["operations", "Operations"], ["marketing", "Marketing"], ["customer", "Support"], ["procurement", "Procurement"], ["legal", "Legal"], ["strategy", "Strategy"], ["meeting", "Meetings"]].map(([r, l]) => (
                        <button key={r} onClick={() => nav(`/app/ceo/department/${r}`)} data-testid={`ceo-quick-${r}`}
                            className="text-[13px] font-medium px-3.5 py-2 rounded-full bg-white border border-black/10 hover:bg-neutral-50 transition-colors">
                            View {l}
                        </button>
                    ))}
                </div>
            </Section>

            <Section title="Prepared for AI" hint="These modules are fully designed and connect in the next phase.">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {AI_PLACEHOLDERS.map(([t, b], i) => (
                        <div key={i} data-testid={`ceo-ai-${i}`} className="relative glass rounded-2xl p-5 overflow-hidden">
                            <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 90% at 100% 0%, rgba(139,92,246,0.08), transparent 55%)" }} />
                            <div className="relative">
                                <div className="flex items-center justify-between">
                                    <span className="font-display text-[16px] font-medium text-neutral-600">{t}</span>
                                    <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">Coming Soon</span>
                                </div>
                                <p className="mt-2 text-[12.5px] text-neutral-400 leading-relaxed">{b}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {ov?.recent_activity?.length > 0 && (
                <Section title="Recent Activity">
                    <FeedList items={ov.recent_activity} />
                </Section>
            )}
        </div>
    );
}

/* ---------- Departments ---------- */
function DepartmentsTab() {
    const nav = useNavigate();
    const [rows, setRows] = useState(null);
    useEffect(() => { ceoApi.departments().then(setRows).catch(() => {}); }, []);
    if (!rows) return <Loading />;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ceo-departments">
            {rows.map((d) => (
                <div key={d.role} data-testid={`ceo-dept-${d.role}`} className="glass rounded-2xl p-5 flex flex-col">
                    <div className="flex items-center justify-between">
                        <span className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center"><ActionIcon name="chart" /></span>
                        <StatusPill status={d.status} />
                    </div>
                    <div className="mt-3 font-display text-[18px] font-medium text-black">{d.label}</div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        {[["Active", d.employees_active], ["Today", d.reports_today], ["Pending", d.pending_reports]].map(([l, v], i) => (
                            <div key={i} className="rounded-xl bg-neutral-50/70 border border-black/5 py-2">
                                <div className="font-display text-lg font-medium text-black">{v}</div>
                                <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-neutral-400">{l}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 text-[11.5px] text-neutral-500">
                        {d.latest_title ? <>Latest: <span className="text-black">{d.latest_title}</span> · {d.latest_employee || "—"} · {fmtDate(d.latest_update)}</> : "No updates yet"}
                    </div>
                    <div className="mt-4 flex gap-2">
                        <Button size="sm" variant="primary" onClick={() => nav(`/app/ceo/department/${d.role}`)} data-testid={`ceo-open-dept-${d.role}`}>Open Department</Button>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---------- Activity ---------- */
function ActivityTab() {
    const [items, setItems] = useState(null);
    useEffect(() => { ceoApi.feed(40).then(setItems).catch(() => {}); }, []);
    if (!items) return <Loading />;
    if (!items.length) return <EmptyState title="No activity yet" hint="Team actions across every department will stream here." />;
    return <div className="glass rounded-2xl p-2" data-testid="ceo-feed"><FeedList items={items} /></div>;
}

function FeedList({ items }) {
    return (
        <ul className="divide-y divide-black/5">
            {items.map((a, i) => (
                <li key={a.id || i} className="flex items-center justify-between gap-4 px-3 py-3" data-testid={`feed-item-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="h-8 w-8 rounded-full bg-violet-50 text-violet-600 inline-flex items-center justify-center text-[11px] font-semibold shrink-0">
                            {(a.user_name || a.user_email || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <div className="min-w-0">
                            <div className="text-[13px] text-black truncate">
                                <span className="font-medium">{a.user_name || a.user_email || "System"}</span>{" "}
                                <span className="text-neutral-500">{String(a.action || "").replace(/_/g, " ")}</span>{" "}
                                {a.target && <span className="text-neutral-700">{a.target}</span>}
                            </div>
                            <div className="text-[11px] text-neutral-400">{a.role || ""} · {fmtDate(a.timestamp)}</div>
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

/* ---------- Submissions ---------- */
function SubmissionsTab() {
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ q: "", dept: "", status: "", priority: "" });
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        setData(await ceoApi.submissions({ ...params, limit: 50 }));
    }, [filters]);
    useEffect(() => { load(); }, [load]);

    const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div data-testid="ceo-submissions">
            <div className="glass rounded-2xl p-3.5 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input placeholder="Search title / ID / employee" value={filters.q} onChange={(e) => set("q", e.target.value)} data-testid="ceo-sub-search" />
                <Select value={filters.dept} onChange={(e) => set("dept", e.target.value)} data-testid="ceo-sub-dept">
                    <option value="">All departments</option>
                    {[["sales", "Sales"], ["finance", "Finance"], ["hr", "HR"], ["operations", "Operations"], ["marketing", "Marketing"], ["customer", "Support"], ["procurement", "Procurement"], ["legal", "Legal"], ["strategy", "Strategy"], ["meeting", "Meetings"]].map(([r, l]) => <option key={r} value={r}>{l}</option>)}
                </Select>
                <Select value={filters.status} onChange={(e) => set("status", e.target.value)} data-testid="ceo-sub-status">
                    <option value="">All statuses</option>
                    {["draft", "submitted", "updated"].map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
                <Select value={filters.priority} onChange={(e) => set("priority", e.target.value)} data-testid="ceo-sub-priority">
                    <option value="">All priorities</option>
                    {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                </Select>
            </div>

            {!data ? <Loading /> : data.items.length === 0 ? (
                <EmptyState title="No submissions found" hint="Adjust filters or wait for teams to submit." />
            ) : (
                <div className="glass rounded-2xl overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1.4fr_0.8fr_1fr_0.9fr_0.7fr_0.9fr] gap-3 px-4 py-3 text-[10px] font-semibold tracking-[0.12em] uppercase text-neutral-400 border-b border-black/5">
                        <span>Title</span><span>Dept</span><span>Employee</span><span>Type</span><span>Priority</span><span>Status</span>
                    </div>
                    <div className="divide-y divide-black/5" data-testid="ceo-sub-rows">
                        {data.items.map((s) => (
                            <button key={s.id} onClick={() => ceoApi.submission(s.id).then(setDetail)} data-testid={`ceo-sub-row-${s.id}`}
                                className="w-full text-left grid grid-cols-1 md:grid-cols-[1.4fr_0.8fr_1fr_0.9fr_0.7fr_0.9fr] gap-1 md:gap-3 md:items-center px-4 py-3 hover:bg-black/[0.02]">
                                <span className="text-[13.5px] font-medium text-black truncate">{s.title}<span className="block md:hidden text-[11px] text-neutral-400 font-mono-alt">{s.submission_id}</span></span>
                                <span className="text-[12px] text-neutral-600">{s.role}</span>
                                <span className="text-[12px] text-neutral-600 truncate">{s.employee_name}</span>
                                <span className="text-[12px] text-neutral-500 truncate">{s.type}</span>
                                <span><PriorityPill priority={s.priority} /></span>
                                <span><StatusPill status={s.status} /></span>
                            </button>
                        ))}
                    </div>
                    <div className="px-4 py-2 text-[11px] text-neutral-400">{data.items.length} of {data.total}</div>
                </div>
            )}

            <SubmissionDetail detail={detail} onClose={() => setDetail(null)} />
        </div>
    );
}

function SubmissionDetail({ detail, onClose }) {
    return (
        <Modal open={!!detail} onClose={onClose} title={detail?.title || "Submission"} size="lg" testid="ceo-sub-detail">
            {detail && (
                <div>
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="text-[11px] font-mono-alt text-neutral-400">{detail.submission_id}</span>
                        <StatusPill status={detail.status} /><PriorityPill priority={detail.priority} />
                        <span className="text-[11px] text-neutral-400">{detail.employee_name} · {detail.role}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {Object.entries(detail.data || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                            <div key={k}>
                                <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400">{k.replace(/_/g, " ")}</div>
                                <div className="text-[14px] text-black mt-0.5 break-words">{String(v)}</div>
                            </div>
                        ))}
                    </div>
                    {detail.attachments?.length > 0 && (
                        <div className="mt-5">
                            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-2">Attachments</div>
                            <ul className="space-y-2">
                                {detail.attachments.map((a) => (
                                    <li key={a.id} className="flex items-center justify-between rounded-xl bg-neutral-50 border border-black/5 px-3 py-2">
                                        <span className="text-[13px] text-black truncate">{a.filename}</span>
                                        <button className="text-violet-600 hover:text-violet-700 text-[12px]" data-testid={`ceo-sub-download-${a.id}`}
                                            onClick={async () => { const f = await ceoApi.attachment(a.id); downloadAttachment(f); }}>Download</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}

/* ---------- Documents ---------- */
function DocumentsTab() {
    const [docs, setDocs] = useState(null);
    const [q, setQ] = useState("");
    useEffect(() => { ceoApi.documents(q ? { q } : {}).then(setDocs); }, [q]);
    return (
        <div data-testid="ceo-documents">
            <div className="mb-5 max-w-md"><Input placeholder="Search documents" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ceo-doc-search" /></div>
            {!docs ? <Loading /> : docs.length === 0 ? <EmptyState title="No documents yet" hint="Files uploaded by any team appear here." /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ceo-doc-grid">
                    {docs.map((d) => (
                        <div key={d.id} className="glass rounded-2xl p-5 flex flex-col">
                            <div className="flex items-center gap-3">
                                <span className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center text-[10px] font-semibold uppercase">{(d.filename.split(".").pop() || "doc").slice(0, 4)}</span>
                                <div className="min-w-0">
                                    <div className="text-[13.5px] font-medium text-black truncate">{d.filename}</div>
                                    <div className="text-[11px] text-neutral-400">{fmtBytes(d.size)} · {fmtDate(d.uploaded_at)}</div>
                                </div>
                            </div>
                            <button onClick={async () => { const f = await ceoApi.attachment(d.id); downloadAttachment(f); }} data-testid={`ceo-doc-download-${d.id}`} className="mt-4 self-start text-violet-600 hover:text-violet-700 text-[12px] font-medium">Download →</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------- Tasks ---------- */
function TasksTab() {
    const [ov, setOv] = useState(null);
    useEffect(() => { ceoApi.tasksOverview().then(setOv); }, []);
    if (!ov) return <Loading />;
    const cards = [["Pending", ov.pending, "warning"], ["Completed", ov.completed], ["High Priority", ov.high_priority, "warning"], ["Overdue", ov.overdue, "danger"], ["Upcoming", ov.upcoming]];
    return (
        <div data-testid="ceo-tasks">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {cards.map(([l, v, tone], i) => (
                    <div key={i} className="glass rounded-2xl p-4" data-testid={`ceo-task-${i}`}>
                        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-neutral-500">{l}</div>
                        <div className={`mt-2 font-display text-2xl font-medium ${tone === "warning" ? "text-amber-600" : tone === "danger" ? "text-rose-600" : "text-black"}`}>{v}</div>
                    </div>
                ))}
            </div>
            {ov.recent.length === 0 ? <EmptyState title="No tasks yet" /> : (
                <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden">
                    {ov.recent.map((t) => (
                        <div key={t.id} className="flex items-center justify-between gap-4 px-4 py-3">
                            <div className="min-w-0"><div className="text-[13.5px] font-medium text-black truncate">{t.title}</div><div className="text-[11px] text-neutral-400">{t.employee_name} · {t.due_date ? `Due ${t.due_date}` : fmtDate(t.created_at)}</div></div>
                            <div className="flex items-center gap-2"><PriorityPill priority={t.priority} /><StatusPill status={t.status} /></div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------- Meetings ---------- */
function MeetingsTab() {
    const [m, setM] = useState(null);
    useEffect(() => { ceoApi.meetings().then(setM); }, []);
    if (!m) return <Loading />;
    const group = (title, items, testid) => (
        <Section title={title}>
            {items.length === 0 ? <div className="text-[13px] text-neutral-400">Nothing here yet.</div> : (
                <div className="glass rounded-2xl divide-y divide-black/5 overflow-hidden" data-testid={testid}>
                    {items.map((s) => (
                        <div key={s.id} className="px-4 py-3">
                            <div className="text-[13.5px] font-medium text-black">{s.title}</div>
                            <div className="text-[11px] text-neutral-400">{s.employee_name} · {fmtDate(s.updated_at)}</div>
                        </div>
                    ))}
                </div>
            )}
        </Section>
    );
    return (
        <div className="space-y-8" data-testid="ceo-meetings">
            {group("Today's Meetings", m.today, "ceo-meet-today")}
            {group("Upcoming Meetings", m.upcoming, "ceo-meet-upcoming")}
            {group("Recent Minutes", m.recent_minutes, "ceo-meet-minutes")}
            {group("Pending Action Items", m.pending_action_items, "ceo-meet-actions")}
        </div>
    );
}

/* ---------- Search ---------- */
function SearchModal({ onClose }) {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    useEffect(() => {
        if (q.length < 2) { setRes(null); return; }
        const t = setTimeout(() => ceoApi.search(q).then(setRes).catch(() => {}), 250);
        return () => clearTimeout(t);
    }, [q]);
    return (
        <Modal open onClose={onClose} title="Search company" size="lg" testid="ceo-search-modal">
            <Input autoFocus placeholder="Employees, reports, documents…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ceo-search-input" />
            {res && (
                <div className="mt-4 space-y-4">
                    <SearchGroup title="Employees" items={res.employees} render={(e) => `${e.name} · ${e.role}`} />
                    <SearchGroup title="Submissions" items={res.submissions} render={(s) => `${s.title} · ${s.submission_id}`} />
                    <SearchGroup title="Documents" items={res.documents} render={(d) => d.filename} />
                    {res.employees.length + res.submissions.length + res.documents.length === 0 && <div className="text-[13px] text-neutral-400">No matches.</div>}
                </div>
            )}
        </Modal>
    );
}
function SearchGroup({ title, items, render }) {
    if (!items?.length) return null;
    return (
        <div>
            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-neutral-400 mb-1.5">{title}</div>
            <ul className="space-y-1">{items.map((it, i) => <li key={i} className="text-[13px] text-black bg-neutral-50 rounded-lg px-3 py-1.5">{render(it)}</li>)}</ul>
        </div>
    );
}

/* ---------- shared ---------- */
function Section({ title, hint, children }) {
    return (
        <section>
            <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-lg font-medium text-black">{title}</h2>
                {hint && <span className="text-[11.5px] text-neutral-400 hidden sm:block">{hint}</span>}
            </div>
            {children}
        </section>
    );
}
function Loading() { return <div className="text-neutral-500 py-16 text-center">Loading…</div>; }
